import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Grid,
  TablePagination,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PaymentIcon from '@mui/icons-material/Payment';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { cyclePeriodFor, dueDateForCycle } from '../../utils/rentCycle';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import EmptyState from '../EmptyState';
import { useCurrency } from '../../context/CurrencyContext';
import LatePenaltyPanel from '../LatePenaltyPanel';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import TenantPicker from '../TenantPicker';
import ListViewControls from '../ListViewControls';


// helper to download or print PDF with auth
const downloadPdfWithAuth = async (invoiceId, doPrint = false) => {
  try {
    const res = await axios.get(`/invoices/${invoiceId}/pdf`, {
      responseType: 'blob'
    });
    
    // Check if response is actually a PDF
    if (res.data.type && res.data.type !== 'application/pdf') {
      // Try to parse as JSON error message
      const text = await res.data.text();
      try {
        const error = JSON.parse(text);
        toast.error(error.message || 'Unable to download invoice PDF');
      } catch {
        toast.error('Server returned non-PDF response');
      }
      return;
    }

    const file = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(file);
    const w = window.open(url, '_blank');
    if (doPrint && w) {
      const onLoad = () => { 
        w.focus(); 
        w.print(); 
        w.removeEventListener('load', onLoad); 
      };
      w.addEventListener('load', onLoad);
    }
  } catch (e) {
    // Try to extract error message from response
    let errorMessage = 'Unable to download invoice PDF';
    if (e.response) {
      if (e.response.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const error = JSON.parse(text);
          errorMessage = error.message || errorMessage;
        } catch {
          errorMessage = `Error ${e.response.status}: ${e.response.statusText}`;
        }
      } else {
        errorMessage = e.response.data?.message || errorMessage;
      }
    } else if (e.message) {
      errorMessage = e.message;
    }
    toast.error(errorMessage);
    console.error('PDF download error:', e);
  }
};

// The statuses an invoice can actually hold — these mirror the model's ENUM exactly.
// 'partially_paid' and 'fully_paid' used to appear here too; they are not valid ENUM
// values, so choosing either failed at the database. The rest, apart from 'cancelled',
// are derived from the balance and were silently reverted the moment they were saved.
// Mirrors backend/utils/accountCategories.js.
const ACCOUNT_CATEGORY_LABELS = { cash: 'Cash', bank: 'Bank', mobile_money: 'Mobile Money', other: 'Other' };

const INVOICE_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
];

// "1 Aug 2026 - 31 Aug 2026", collapsing to one month name when the period is exactly a
// calendar month, which is what a scheduled rent invoice always is.
const formatPeriod = (from, to) => {
  const start = new Date(from);
  const end = new Date(to);
  if (isNaN(start) || isNaN(end)) return '';
  const isWholeMonth = start.getDate() === 1
    && start.getMonth() === end.getMonth()
    && start.getFullYear() === end.getFullYear()
    && end.getDate() === new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
  return isWholeMonth
    ? format(start, 'MMMM yyyy')
    : `${format(start, 'd MMM yyyy')} - ${format(end, 'd MMM yyyy')}`;
};

// Used only if /charge-types cannot be reached. These are the codes every existing invoice
// already carries, so the form still works rather than offering nothing.
const FALLBACK_TYPES = [
  { value: 'rent', label: 'Rent' },
  { value: 'utility', label: 'Utility' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'repair', label: 'Repair' },
  { value: 'late_fee', label: 'Late penalty' },
  { value: 'other', label: 'Other' }
];

const InvoiceManagement = () => {
  // The Type dropdowns are filled from charge_types, not a list in this file. They used to
  // be hardcoded here AND as a database ENUM, so adding one meant a code change and a
  // migration.
  //
  // Falls back to the seeded codes if the call fails: a form that cannot offer a type
  // cannot raise an invoice at all, and the six below are what every existing record
  // already uses.
  const [chargeTypes, setChargeTypes] = useState(FALLBACK_TYPES);
  // The pay dialog on this screen records a PAYMENT, so it needs the payment list — which
  // excludes late_fee, because a penalty is charged rather than paid as a category. One
  // list for both dropdowns offered a type the server would refuse.
  const [paymentTypes, setPaymentTypes] = useState(
    FALLBACK_TYPES.filter((t) => t.value !== 'late_fee')
  );

  const fetchChargeTypes = async (usage) => {
    try {
      const res = await axios.get(`/charge-types?usage=${usage}`);
      const rows = (res.data || []).map((t) => ({ value: t.code, label: t.name }));
      if (!rows.length) return;
      if (usage === 'payment') setPaymentTypes(rows);
      else setChargeTypes(rows);
    } catch (e) {
      // Left on the fallback deliberately, and quietly — an error toast on a screen the
      // user opened for something else is noise they cannot act on.
    }
  };

  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [open, setOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusInvoice, setStatusInvoice] = useState(null);
  const [statusValue, setStatusValue] = useState('pending');
  const [editing, setEditing] = useState(null);
  // Posted rent invoices are expanded via /expand-period (amount = months × rent), not PUT.
  const [editingMeta, setEditingMeta] = useState(null);
  const [expandPreview, setExpandPreview] = useState(null);
  // Why the server could not price the period — usually that it is not whole rent
  // months — shown beside the dates so it is fixed before Save, not after.
  const [previewError, setPreviewError] = useState('');
  const [formData, setFormData] = useState({
    tenant: '',
    property: '',
    amount: 0,
    dueDate: '',
    periodStart: '',
    periodEnd: '',
    invoiceType: 'rent',
    items: [{ description: '', quantity: 1, unitPrice: 0, total: 0 }],
  });
  const { formatMoney: formatCurrency } = useCurrency();
  const [itemDesc, setItemDesc] = useState('');

  const isExpandableRent = Boolean(
    editing
    && editingMeta
    && (editingMeta.invoiceType || 'rent') === 'rent'
    && editingMeta.journalEntryId
    && editingMeta.status !== 'cancelled'
    && editingMeta.status !== 'draft'
    && !(editingMeta.status === 'paid' && Number(editingMeta.balance) <= 0)
  );

  // Pay-against-invoice dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null);
  const [payForm, setPayForm] = useState({
    amountPaid: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentType: 'rent',
    // How the money arrived and which account received it. The server REQUIRES both once a
    // payment is marked received, because that is when the journal entry is written and the
    // debit has to land in a real account. Without them this dialog could not record a
    // payment at all.
    paymentModeId: '',
    receivedIntoAccountId: '',
    // The months the rent covers. Defaulted from the invoice being paid, which is the
    // whole point of paying from here — the invoice already knows its period.
    periodStart: '',
    periodEnd: '',
    staffComments: ''
  });
  const [paymentModes, setPaymentModes] = useState([]);
  const [assetAccounts, setAssetAccounts] = useState([]);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeInvoice, setCloseInvoice] = useState(null);
  const [closePreview, setClosePreview] = useState(null);
  const [closeReason, setCloseReason] = useState('');
  const [payPenalty, setPayPenalty] = useState({ enabled: false, amount: '', reason: '', percent: null });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  // Which set of records the table is showing, and the date window over it.
  const [view, setView] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  useEffect(() => {
    fetchInvoices();
    fetchPaymentModes();
    fetchAssetAccounts();
    fetchChargeTypes('invoice');
    fetchChargeTypes('payment');
    // Initial load only — paging is driven explicitly by the pagination handlers, which
    // pass the page and size they want rather than relying on state having settled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Both fail soft: an empty list makes the selector say what is missing and who can add
  // it, which is more use than an error toast on a screen the user came to for invoices.
  const fetchPaymentModes = async () => {
    try {
      const response = await axios.get('/payment-modes?isActive=true');
      setPaymentModes(response.data || []);
    } catch (error) {
      setPaymentModes([]);
    }
  };

  const fetchAssetAccounts = async () => {
    try {
      const response = await axios.get('/accounting/chart-of-accounts');
      const all = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setAssetAccounts(all.filter((a) => a.type === 'asset' && a.isActive !== false));
    } catch (error) {
      setAssetAccounts([]);
    }
  };

  // The API pages at 50 by default. This used to request no page at all and render the
  // result as if it were everything, so any invoice past the 50th was invisible with
  // nothing on screen to suggest more existed.
  const fetchInvoices = async (targetPage = page, targetSize = rowsPerPage, overrides = {}) => {
    try {
      const params = {
        page: targetPage + 1,
        limit: targetSize,
        ...(({ search: q, status, invoiceType, view: v, from, to }) => ({
          ...(q ? { search: q } : {}),
          ...(status ? { status } : {}),
          ...(invoiceType ? { invoiceType } : {}),
          // 'all' is the server's default, so it is left off rather than sent as a no-op.
          ...(v && v !== 'all' ? { view: v } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {})
        }))({
          search: overrides.search ?? search,
          status: overrides.status ?? statusFilter,
          invoiceType: overrides.invoiceType ?? typeFilter,
          view: overrides.view ?? view,
          from: overrides.from ?? dateRange.from,
          to: overrides.to ?? dateRange.to
        })
      };
      // Searched on the SERVER. The list pages at fifty, so filtering the rows already here
      // would report an invoice on page 7 as missing rather than as elsewhere.
      const response = await axios.get('/invoices', { params });
      const body = response.data;
      if (Array.isArray(body)) {
        setInvoices(body);
        setTotalInvoices(body.length);
      } else {
        setInvoices(body.data || []);
        setTotalInvoices(body.total ?? (body.data || []).length);
      }
    } catch (error) {
      toast.error('Error fetching invoices');
    }
  };

  // Debounced: a request per keystroke races, and the answer for "INV-01" can land after
  // the answer for "INV-012".
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchInvoices(0, rowsPerPage, { search });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const applyFilter = (changes) => {
    setPage(0);
    fetchInvoices(0, rowsPerPage, changes);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    fetchInvoices(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    setPage(0);
    fetchInvoices(0, size);
  };

  const handleOpen = (invoice = null) => {
    setExpandPreview(null);
    if (invoice) {
      setEditing(invoice._id);
      setEditingMeta({
        journalEntryId: invoice.journalEntryId || null,
        status: invoice.status,
        balance: invoice.balance,
        paidAmount: invoice.paidAmount,
        invoiceType: invoice.invoiceType || 'rent',
        invoiceNumber: invoice.invoiceNumber
      });
      setFormData({
        tenant: invoice.tenant._id || invoice.tenant,
        property: invoice.property._id || invoice.property,
        amount: invoice.amount,
        dueDate: new Date(invoice.dueDate).toISOString().split('T')[0],
        // DATEONLY comes back as 'YYYY-MM-DD' already, which is what a date input wants.
        periodStart: invoice.periodStart || '',
        periodEnd: invoice.periodEnd || '',
        invoiceType: invoice.invoiceType || 'rent',
        items: invoice.items || [
          { description: '', quantity: 1, unitPrice: 0, total: 0 },
        ],
      });
      if (invoice.items && invoice.items.length > 0) {
        setItemDesc(invoice.items[0].description && invoice.items[0].description.toLowerCase() !== 'rent' ? invoice.items[0].description : '');
      } else {
        setItemDesc('');
      }
    } else {
      setEditing(null);
      setEditingMeta(null);
      setFormData({
        tenant: '',
        property: '',
        amount: 0,
        dueDate: '',
        periodStart: '',
        periodEnd: '',
        invoiceType: 'rent',
        items: [{ description: '', quantity: 1, unitPrice: 0, total: 0 }],
      });
      setItemDesc('');
    }
    setOpen(true);
  };

  // Preview months × rent when creating or expanding a multi-month rent invoice.
  const wantsRentPreview = open
    && (formData.invoiceType || 'rent') === 'rent'
    && formData.periodStart
    && formData.periodEnd
    && formData.periodEnd >= formData.periodStart
    && (isExpandableRent || (!editing && formData.tenant));

  useEffect(() => {
    if (!wantsRentPreview) {
      setExpandPreview(null);
      setPreviewError('');
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = isExpandableRent
          ? await axios.get(`/invoices/${editing}/expand-preview`, {
            params: { periodStart: formData.periodStart, periodEnd: formData.periodEnd }
          })
          : await axios.get('/invoices/rent-preview', {
            params: {
              tenantId: formData.tenant,
              periodStart: formData.periodStart,
              periodEnd: formData.periodEnd
            }
          });
        if (!cancelled) {
          setExpandPreview(data);
          setPreviewError('');
          const amount = data.newAmount ?? data.suggestedAmount;
          if (amount != null) setFormData((prev) => ({ ...prev, amount }));
        }
      } catch (e) {
        if (!cancelled) {
          setExpandPreview(null);
          setPreviewError(e.response?.data?.message || '');
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, wantsRentPreview, isExpandableRent, editing, formData.tenant, formData.periodStart, formData.periodEnd]);

  const handleSubmit = async (asDraft = false) => {
    try {
      // Validate form data
      if (!formData.tenant) {
        toast.error('Please select a tenant');
        return;
      }
      if (!formData.dueDate) {
        toast.error('Please select a due date');
        return;
      }
      if (!formData.amount || formData.amount <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }

      // The property comes from formData, which TenantPicker fills in when a tenant is
      // chosen (and handleOpen fills in when editing).
      //
      // This used to look the tenant up in a locally held list and refuse with "Selected
      // tenant not found" if it missed. That list was one page of fifty, so the lookup
      // failed for exactly the tenants the server-side picker was added to reach — pick
      // someone past the fiftieth and the invoice could not be saved.
      const propertyId = formData.property;
      if (!propertyId) {
        toast.error('This tenant is not assigned to a property, so there is nothing to invoice against.');
        return;
      }

      const invoiceType = formData.invoiceType || 'rent';

      // Only rent is billed for a stretch of tenancy; the server enforces the same rule.
      if (invoiceType === 'rent') {
        if (!formData.periodStart || !formData.periodEnd) {
          toast.error('Please give the period this rent covers, from and to');
          return;
        }
        if (formData.periodEnd < formData.periodStart) {
          toast.error('Period end cannot be before period start');
          return;
        }
      }

      const desc = invoiceType === 'other' ? (itemDesc || 'Other') :
                   invoiceType === 'rent' ? 'Rent' :
                   invoiceType === 'utility' ? 'Utility' :
                   invoiceType === 'maintenance' ? 'Maintenance' :
                   invoiceType === 'repair' ? 'Repair' : 'Other';
      
      const invoiceData = {
        tenant: formData.tenant,
        property: propertyId,
        dueDate: formData.dueDate,
        amount: parseFloat(formData.amount),
        invoiceType: invoiceType,
        periodStart: invoiceType === 'rent' ? formData.periodStart : null,
        periodEnd: invoiceType === 'rent' ? formData.periodEnd : null,
        items: [{ 
          description: desc, 
          quantity: 1, 
          unitPrice: parseFloat(formData.amount), 
          total: parseFloat(formData.amount) 
        }],
      };

      if (editing && isExpandableRent) {
        // Posted rent: expand period and recalc amount from months × rent, then the
        // accountant records payment against this invoice (partial leaves a balance due).
        // The due date travels with it: it is the one other field still editable on a
        // posted invoice, and this request used to leave it out, so edits never saved.
        const { data } = await axios.post(`/invoices/${editing}/expand-period`, {
          periodStart: formData.periodStart,
          periodEnd: formData.periodEnd,
          dueDate: formData.dueDate
        });
        toast.success(data.message || 'Invoice period expanded');
      } else if (editing) {
        await axios.put(`/invoices/${editing}`, invoiceData);
        toast.success('Invoice updated successfully');
      } else {
        // A draft is saved and nothing else: no ledger entry, no PDF, no email to the
        // tenant. It is the only state an invoice can be deleted from, so it is also the
        // safe place to park one that is not ready.
        const { data } = await axios.post('/invoices', { ...invoiceData, ...(asDraft ? { status: 'draft' } : {}) });
        // The server's message says whether the tenant was told, which the generic
        // "created successfully" does not — and showing both is two toasts saying the
        // same thing, with the half that matters second.
        if (data?.message) toast.success(data.message);
        else toast.success('Invoice created successfully');
      }
      setOpen(false);
      fetchInvoices();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error saving invoice';
      toast.error(errorMessage);
      console.error('Error saving invoice:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      paid: 'success',
      partial: 'info',
      partially_paid: 'info',
      fully_paid: 'success',
      overdue: 'error',
      cancelled: 'default',
      draft: 'warning',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const opt = INVOICE_STATUS_OPTIONS.find((o) => o.value === status);
    return opt ? opt.label : (status || '').replace(/_/g, ' ');
  };

  // Cancelling is the only status a person sets. Everything else follows from the balance,
  // so the dialog no longer offers a choice that would not stick.
  const handleOpenStatusDialog = (invoice) => {
    setStatusInvoice(invoice);
    setStatusValue('cancelled');
    setStatusDialogOpen(true);
  };

  const handleStatusSubmit = async () => {
    if (!statusInvoice) return;
    try {
      await axios.put(`/invoices/${statusInvoice._id}`, { status: statusValue });
      toast.success('Invoice status updated');
      setStatusDialogOpen(false);
      setStatusInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating status');
    }
  };


  const handleOpenPay = (invoice) => {
    setPayInvoice(invoice);
    setPayPenalty({ enabled: false, amount: '', reason: '', percent: null });
    // The period comes off the invoice being paid. That is the advantage of paying from
    // here rather than from the payments screen: the months are already known, and typing
    // them again is a chance to disagree with the invoice.
    const asDay = (d) => (d ? new Date(d).toISOString().split('T')[0] : '');
    setPayForm({
      amountPaid: invoice.balance || invoice.amount,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentType: invoice.invoiceType || 'rent',
      paymentModeId: '',
      receivedIntoAccountId: '',
      periodStart: asDay(invoice.periodStart),
      periodEnd: asDay(invoice.periodEnd),
      staffComments: '',
    });
    setPayOpen(true);
  };

  // Closing an invoice short is the only action here that gives money away, so it asks the
  // server what it would do before offering to do it — the limit, the shortfall and the
  // agency fee at stake all come back from one preview call rather than being guessed at
  // in the browser and contradicted on submit.
  const handleOpenClose = async (invoice) => {
    setCloseInvoice(invoice);
    setCloseReason('');
    setClosePreview(null);
    setCloseOpen(true);
    try {
      const { data } = await axios.get(`/invoices/${invoice._id}/settle-close`);
      setClosePreview(data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not check this invoice');
      setCloseOpen(false);
    }
  };

  const handleConfirmClose = async () => {
    if (!closeReason.trim()) { toast.error('Give a reason for closing this invoice short'); return; }
    try {
      const { data } = await axios.post(`/invoices/${closeInvoice._id}/settle-close`, { reason: closeReason });
      toast.success(data.message);
      setCloseOpen(false);
      setCloseInvoice(null);
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to close invoice');
    }
  };

  // A draft is not on the ledger and the tenant has not been told, which is what makes
  // both of these safe. Neither is offered on anything else.
  const handleIssue = async (invoice) => {
    if (!window.confirm(`Issue invoice ${invoice.invoiceNumber}? It will be posted to the ledger and the tenant notified.`)) return;
    try {
      const { data } = await axios.post(`/invoices/${invoice._id}/issue`);
      toast.success(data.message);
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not issue this invoice');
    }
  };

  // Sends regardless of the automatic-sending setting. That setting governs what the
  // system does on its own; this is a person asking, and a button that silently did
  // nothing while the toggle was off would be the worst of both.
  const handleSend = async (invoice) => {
    if (!window.confirm(`Send ${invoice.invoiceNumber} to ${invoice.tenant?.name || 'the tenant'}?`)) return;
    try {
      const { data } = await axios.post(`/invoices/${invoice._id}/send`);
      toast.success(data.message || 'Invoice sent');
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not send this invoice');
    }
  };

  const handleDeleteDraft = async (invoice) => {
    // Asks why rather than just whether. The reason is the one column on the Deleted view
    // that cannot be reconstructed later, and prompt() doubles as the confirmation, so
    // this is one dialog rather than two.
    //
    // No longer says "cannot be undone": the delete is soft, and the draft stays visible
    // under Deleted with who removed it and when.
    const reason = window.prompt(
      `Delete draft ${invoice.invoiceNumber}?\n\n`
      + 'It will stop appearing in the invoice list but stays on record under the Deleted '
      + 'view. Why is it being deleted? (optional)'
    );
    if (reason === null) return;
    try {
      const { data } = await axios.delete(`/invoices/${invoice._id}`, { data: { reason } });
      toast.success(data.message);
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not delete this draft');
    }
  };

  const handlePay = async () => {
    if (!payForm.amountPaid || payForm.amountPaid <= 0) { toast.error('Enter a valid amount'); return; }
    // Checked here as well as on the server so the message names the field, rather than
    // arriving as a generic failure after the dialog has already tried to submit.
    if (!payForm.paymentModeId) { toast.error('Choose how the money was received'); return; }
    if (!payForm.receivedIntoAccountId) { toast.error('Choose which account the money was received into'); return; }
    // Only rent is billed by period; the server drops a period sent on anything else.
    if (payForm.paymentType === 'rent' && (!payForm.periodStart || !payForm.periodEnd)) {
      toast.error('Give the period this rent covers, from and to'); return;
    }
    if (payPenalty.enabled && !String(payPenalty.reason || '').trim()) {
      toast.error('Give a reason for the late payment penalty'); return;
    }
    try {
      await axios.post('/payments', {
        tenant: payInvoice.tenant?._id || payInvoice.tenant,
        invoiceId: payInvoice._id,
        amountPaid: parseFloat(payForm.amountPaid),
        paymentDate: payForm.paymentDate,
        paymentType: payForm.paymentType,
        // Drives the journal entry: the debit goes to whichever account is chosen here.
        paymentModeId: payForm.paymentModeId,
        receivedIntoAccountId: payForm.receivedIntoAccountId,
        periodStart: payForm.periodStart || undefined,
        periodEnd: payForm.periodEnd || undefined,
        status: 'received',
        staffComments: payForm.staffComments,
        // Only sent when someone actually ticked it — the server treats its absence as
        // 'no penalty' rather than 'decide for me'.
        ...(payPenalty.enabled
          ? { latePenalty: { amount: parseFloat(payPenalty.amount) || 0, percent: payPenalty.percent, reason: payPenalty.reason } }
          : {}),
      });
      toast.success('Payment recorded and applied to invoice');
      setPayOpen(false);
      setPayInvoice(null);
      fetchInvoices();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to record payment');
    }
  };

  // Cancelled and deleted are different events with parallel records — one voids a posted
  // invoice and reverses its ledger, the other removes a draft that never reached it. The
  // columns are the same three questions, so the table reads whichever set applies rather
  // than carrying six columns for two mutually exclusive cases.
  // Rent must state the months it covers, and the invoice normally answers that — the
  // create route refuses a rent invoice without a period, so one is there to read. This is
  // only true when it is not: a legacy invoice, or the type switched to Rent on an invoice
  // that never carried one.
  const needsManualPeriod = payForm.paymentType === 'rent'
    && !(payForm.periodStart && payForm.periodEnd);

  // Only accounts of the kind the chosen mode receives into — a bank account cannot be
  // recorded against a cash payment. The server enforces the same rule; offering the wrong
  // ones here would just invite a rejection.
  const selectedMode = paymentModes.find((m) => m._id === payForm.paymentModeId) || null;
  const accountsForSelectedMode = selectedMode
    ? assetAccounts.filter((a) => a.accountCategory === selectedMode.accountCategory)
    : [];
  const selectedModeCategoryLabel = selectedMode
    ? (ACCOUNT_CATEGORY_LABELS[selectedMode.accountCategory] || selectedMode.accountCategory)
    : '';

  const showAudit = view === 'cancelled' || view === 'deleted';
  const auditLabel = view === 'deleted' ? 'Deleted' : 'Cancelled';
  const auditDate = (inv) => (view === 'deleted' ? inv.deletedAt : inv.cancelledAt);
  const auditWho = (inv) => (view === 'deleted' ? inv.deletedBy?.name : inv.cancelledBy?.name);
  const auditReason = (inv) => (view === 'deleted' ? inv.deletionReason : inv.cancellationReason);

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Invoice Management</Typography>
        {/* Not on the Cancelled or Deleted views: an invoice generated there would be
            created and then not appear, since neither view lists live ones. */}
        {view !== 'cancelled' && view !== 'deleted' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Generate Invoice
          </Button>
        )}
      </Box>

      {/* One table throughout — these switch which set of records it holds. Deleted rows
          are soft-deleted and excluded from every other view, so this is the only way to
          reach them. */}
      <ListViewControls
        view={view}
        onViewChange={(v) => {
          setView(v);
          // Status is the finer filter INSIDE a view; carrying "Paid" over into Cancelled
          // would silently return nothing and read as "no cancelled invoices".
          setStatusFilter('');
          setPage(0);
          fetchInvoices(0, rowsPerPage, { view: v, status: '' });
        }}
        from={dateRange.from}
        to={dateRange.to}
        onDateChange={(next) => {
          setDateRange(next);
          setPage(0);
          fetchInvoices(0, rowsPerPage, next);
        }}
        dateLabel="Invoiced"
      />

      <TableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Invoice number or tenant"
        onReset={() => {
          setSearch(''); setStatusFilter(''); setTypeFilter('');
          setView('all'); setDateRange({ from: '', to: '' });
          applyFilter({ search: '', status: '', invoiceType: '', view: 'all', from: '', to: '' });
        }}
        filters={[
          { key: 'status', label: 'Status', value: statusFilter,
            onChange: (v) => { setStatusFilter(v); applyFilter({ status: v }); },
            options: INVOICE_STATUS_OPTIONS },
          { key: 'invoiceType', label: 'Type', value: typeFilter,
            onChange: (v) => { setTypeFilter(v); applyFilter({ invoiceType: v }); },
            options: chargeTypes }
        ]}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Invoice Number</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Paid</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              {/* Whether the tenant has actually been told. Only meaningful with automatic
                  sending off, but shown always: an invoice nobody sent looks exactly like
                  one that was sent, and staff would otherwise assume every tenant knows. */}
              <TableCell>Sent</TableCell>
              {/* Only where they mean something. On a live invoice these three are empty by
                  definition, and three permanently blank columns on an already-wide table
                  cost width that the columns people came for have to give up. */}
              {showAudit && <TableCell>{auditLabel} on</TableCell>}
              {showAudit && <TableCell>By</TableCell>}
              {showAudit && <TableCell>Reason</TableCell>}
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice._id}>
                <TableCell className="pin-left">{invoice.invoiceNumber}</TableCell>
                <TableCell>
                  {invoice.tenant?.name || 'N/A'}
                </TableCell>
                <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    color={invoice.balance > 0 ? 'error' : 'success'}
                    fontWeight="bold"
                  >
                    {formatCurrency(invoice.balance || invoice.amount)}
                  </Typography>
                  {invoice.writeOffAmount > 0 && (
                    // Shown on the row, not buried in a detail view: a settled invoice that
                    // was settled by waiving part of it is a different fact from one paid
                    // in full, and the difference should be visible without asking.
                    <Typography variant="caption" display="block" color="text.secondary">
                      {formatCurrency(invoice.writeOffAmount)} waived
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {formatCurrency(invoice.paidAmount || 0)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {format(new Date(invoice.dueDate), 'MMM dd, yyyy')}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(invoice.status)}
                    color={getStatusColor(invoice.status)}
                    size="small"
                  />
                  {/* Rent or fee terms moved under a month billed in advance. The invoice
                      stands at the rate the tenant paid; whether to bill the difference is
                      a decision, so it is flagged for one rather than restated. */}
                  {invoice.needsReview && (
                    <Tooltip title={invoice.reviewReason || 'Raised in advance; something changed underneath it'}>
                      <Chip label="Review" color="warning" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={invoice.invoiceType || 'rent'} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{invoice.items && invoice.items[0]?.description}</TableCell>
                <TableCell>
                  {invoice.sentAt ? (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={format(new Date(invoice.sentAt), 'd MMM yyyy')}
                    />
                  ) : invoice.status === 'draft' ? (
                    /* A draft is not "unsent" in the sense that needs chasing — it is
                       deliberately not sent, and flagging it would train people to ignore
                       the column. */
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  ) : (
                    <Chip size="small" color="warning" variant="outlined" label="Not sent" />
                  )}
                </TableCell>
                {showAudit && (
                  <TableCell>
                    {auditDate(invoice)
                      ? format(new Date(auditDate(invoice)), 'd MMM yyyy')
                      : <Typography variant="caption" color="text.secondary">not recorded</Typography>}
                  </TableCell>
                )}
                {showAudit && (
                  <TableCell>
                    {auditWho(invoice)
                      || <Typography variant="caption" color="text.secondary">unknown</Typography>}
                  </TableCell>
                )}
                {showAudit && (
                  <TableCell>
                    {auditReason(invoice)
                      || <Typography variant="caption" color="text.secondary">none given</Typography>}
                  </TableCell>
                )}
                <TableCell className="pin-right">
                  {/* A deleted invoice is a record, not a working document. Editing one or
                      changing its status would write to a row every other screen treats as
                      gone, so the actions are withheld rather than offered and refused. */}
                  {view !== 'deleted' && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenStatusDialog(invoice)}
                      >
                        Update status
                      </Button>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpen(invoice)}
                      >
                        Edit
                      </Button>
                    </>
                  )}
                  {/* Only for an issued invoice that has not reached the tenant. Sending a
                      draft is refused by the server, and re-sending one already sent is a
                      duplicate the tenant did not ask for — reachable from Edit if genuinely
                      needed. */}
                  {!invoice.sentAt && invoice.status !== 'draft' && invoice.status !== 'cancelled'
                    && view !== 'deleted' && (
                    <Button
                      size="small"
                      startIcon={<SendIcon />}
                      onClick={() => handleSend(invoice)}
                    >
                      Send
                    </Button>
                  )}
                  {/* Not payable until issued — a draft has no ledger entry to settle. */}
                  {invoice.balance > 0 && invoice.status !== 'draft' && (
                    <Button
                      size="small"
                      startIcon={<PaymentIcon />}
                      color="success"
                      variant="outlined"
                      onClick={() => handleOpenPay(invoice)}
                    >
                      Pay
                    </Button>
                  )}
                  {invoice.status === 'draft' && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SendIcon />}
                        onClick={() => handleIssue(invoice)}
                      >
                        Issue
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteDraft(invoice)}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                  {/* Only for an invoice the tenant has part paid. Closing an untouched
                      invoice short is not a near miss, and the server would refuse it —
                      offering the button anyway would just invite a rejection. */}
                  {invoice.balance > 0 && invoice.paidAmount > 0 && (
                    <Button
                      size="small"
                      startIcon={<DoneAllIcon />}
                      color="warning"
                      onClick={() => handleOpenClose(invoice)}
                    >
                      Close short
                    </Button>
                  )}
                  <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadPdfWithAuth(invoice._id)}>PDF</Button>
                  <Button size="small" startIcon={<PrintIcon />} onClick={() => downloadPdfWithAuth(invoice._id, true)}>Print</Button>
                </TableCell>
              </TableRow>
            ))}
            {invoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={showAudit ? 14 : 11}>
                  <EmptyState title="No invoices yet" description="Invoices you create or that are generated automatically will show up here." />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollTable>
      <TablePagination
        component="div"
        count={totalInvoices}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
      />

      {/* Close short — waive a small shortfall so the month can be settled */}
      <Dialog open={closeOpen} onClose={() => setCloseOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Close invoice short</DialogTitle>
        <DialogContent>
          {!closePreview && (
            <Typography variant="body2" sx={{ mt: 1 }} color="textSecondary">Checking this invoice…</Typography>
          )}
          {closePreview && (
            <>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Invoice {closePreview.invoiceNumber} · {formatCurrency(closePreview.amount)} billed,
                {' '}{formatCurrency(closePreview.paidAmount)} paid.
              </Typography>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>{formatCurrency(closePreview.shortfall)}</strong> will be written off as a
                  waiver and the invoice marked settled. This cannot be undone from here.
                </Typography>
                {closePreview.feeWouldBeRecognised > 0 && (
                  // The reason the action exists. Staff should see the consequence before
                  // agreeing to it, not discover it in the ledger afterwards.
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    The agency fee of <strong>{formatCurrency(closePreview.feeWouldBeRecognised)}</strong> for
                    this month becomes earned — it is a fixed fee, so it is only earned once the
                    month is settled in full.
                  </Typography>
                )}
              </Box>

              {!closePreview.eligible && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  {closePreview.shortfall <= 0
                    ? 'This invoice is already settled.'
                    : `Too much is outstanding. Up to ${formatCurrency(closePreview.limit)} `
                      + `(${closePreview.limitPercent}% of the invoice) can be closed this way, and `
                      + `${formatCurrency(closePreview.shortfall)} is still owed. Record a waiver on the `
                      + 'tenant ledger instead, where a larger write-off is captured as one.'}
                </Typography>
              )}

              <TextField
                fullWidth
                required
                label="Reason"
                placeholder="e.g. Tenant vacated, balance not worth pursuing"
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                disabled={!closePreview.eligible}
                sx={{ mt: 3 }}
                helperText="Recorded on the invoice and against the waiver in the ledger"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseOpen(false)}>Leave open</Button>
          <Button
            onClick={handleConfirmClose}
            variant="contained"
            color="warning"
            disabled={!closePreview?.eligible || !closeReason.trim()}
          >
            Write off and close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel invoice dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Cancelling voids this invoice. Its status stops tracking the balance and it is
            excluded from arrears.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Paid, partial and overdue are worked out from the balance as payments are
            allocated, so they cannot be set by hand.
          </Typography>
          {statusInvoice && (
            <Typography variant="body2" sx={{ mt: 2, fontWeight: 500 }}>
              Invoice {statusInvoice.invoiceNumber} · {formatCurrency(statusInvoice.amount)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Keep invoice</Button>
          <Button onClick={handleStatusSubmit} variant="contained" color="error">
            Cancel invoice
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editing
            ? (isExpandableRent ? 'Expand rent period (advance)' : 'Edit Invoice')
            : 'Generate New Invoice'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {(isExpandableRent || (!editing && (formData.invoiceType || 'rent') === 'rent')) && (
              <Grid item xs={12}>
                <Alert severity="info">
                  {isExpandableRent ? (
                    <>
                      Widen <strong>Period To</strong> to cover every month the tenant is paying
                      for. The amount becomes months × current monthly rent
                      {expandPreview
                        ? ` (${expandPreview.months} × ${formatCurrency(expandPreview.monthlyRent)} = ${formatCurrency(expandPreview.newAmount)}; ${formatCurrency(expandPreview.newBalance)} would remain due after any amount already paid)`
                        : ''}.
                      Then record the payment against this invoice. A partial payment leaves the
                      balance due.
                    </>
                  ) : (
                    <>
                      Set the period to every month being billed (one month or several), using
                      the tenant's own rent month. The dates are kept exactly as entered. The
                      amount is suggested as months × monthly rent
                      {expandPreview
                        ? ` (${expandPreview.months} × ${formatCurrency(expandPreview.monthlyRent)} = ${formatCurrency(expandPreview.suggestedAmount)})`
                        : ''}.
                      No separate due-month invoice is required first.
                    </>
                  )}
                  {' '}The scheduler will not raise another rent invoice for months inside this period.
                </Alert>
              </Grid>
            )}
            {previewError && (
              <Grid item xs={12}>
                <Alert severity="warning">{previewError}</Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <TenantPicker
                valueId={formData.tenant}
                required
                disabled={isExpandableRent}
                onChange={(val) => {
                  // A new invoice opens on the tenant's current rent month — 10th to 9th
                  // for someone whose cycle starts on the 10th — due on their pay day. The
                  // accountant can still change any of it.
                  const cycle = val && !editing ? cyclePeriodFor(val.billingCycleDay) : null;
                  setFormData({
                    ...formData,
                    tenant: val?._id || '',
                    property: val?.property?._id || val?.unit?.property?._id || val?.unit?.property || '',
                    amount: val?.unit?.monthlyRent || val?.property?.monthlyRent || 0,
                    ...(cycle ? {
                      periodStart: cycle.periodStart,
                      periodEnd: cycle.periodEnd,
                      dueDate: dueDateForCycle(cycle.periodStart, val.paymentDate),
                    } : {}),
                  });
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                select 
                fullWidth 
                label="Invoice Type" 
                value={formData.invoiceType || 'rent'} 
                disabled={isExpandableRent}
                onChange={e => setFormData({ ...formData, invoiceType: e.target.value })}
              >
                {chargeTypes.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* Only rent is billed for a stretch of tenancy. A utility bill or a repair
                charge has no rental period, so the fields are hidden for those types. */}
            {(formData.invoiceType || 'rent') === 'rent' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="date"
                    label="Period From"
                    InputLabelProps={{ shrink: true }}
                    value={formData.periodStart || ''}
                    onChange={e => setFormData({ ...formData, periodStart: e.target.value })}
                    helperText="First day of the rent period, kept exactly as entered"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="date"
                    label="Period To"
                    InputLabelProps={{ shrink: true }}
                    value={formData.periodEnd || ''}
                    onChange={e => setFormData({ ...formData, periodEnd: e.target.value })}
                    inputProps={{ min: formData.periodStart || undefined }}
                    error={!!formData.periodEnd && !!formData.periodStart && formData.periodEnd < formData.periodStart}
                    helperText={isExpandableRent ? 'Last day of the final rent month being paid for' : 'Last day of the rent period (the 9th for a month that starts on the 10th)'}
                  />
                </Grid>
              </>
            )}
            {formData.invoiceType === 'other' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={itemDesc}
                  onChange={e => setItemDesc(e.target.value)}
                  multiline rows={2}
                  required
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onFocus={(e) => e.target.select()}
                disabled={isExpandableRent}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    amount: parseFloat(e.target.value) || 0,
                  })
                }
                helperText={isExpandableRent ? 'Calculated from months × monthly rent' : undefined}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={formData.dueDate}
                onChange={(e) =>
                  setFormData({ ...formData, dueDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          {!editing && (
            <Button onClick={() => handleSubmit(true)}>Save as draft</Button>
          )}
          <Button onClick={() => handleSubmit(false)} variant="contained">
            {editing
              ? (isExpandableRent ? 'Save period and amount' : 'Update')
              : 'Create and issue'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Record Payment Dialog */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {payInvoice && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2"><strong>Invoice:</strong> {payInvoice.invoiceNumber}</Typography>
              <Typography variant="body2"><strong>Tenant:</strong> {payInvoice.tenant?.name}</Typography>
              <Typography variant="body2"><strong>Outstanding:</strong> {formatCurrency(payInvoice.balance)}</Typography>
              {/* Shown because the payment records it: it is what stops the scheduler
                  billing a tenant who has already paid for these months. Read from the
                  invoice, so it cannot drift from what is being settled. */}
              {payForm.periodStart && payForm.periodEnd && (
                <Typography variant="body2">
                  <strong>Period:</strong> {formatPeriod(payForm.periodStart, payForm.periodEnd)}
                </Typography>
              )}
            </Box>
          )}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Amount" type="number"
                value={payForm.amountPaid}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setPayForm({ ...payForm, amountPaid: parseFloat(e.target.value) || 0 })}
                helperText={payInvoice && payForm.amountPaid > payInvoice.balance ? 'Excess will remain as unallocated credit' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth select label="Payment Type"
                value={payForm.paymentType}
                onChange={(e) => setPayForm({ ...payForm, paymentType: e.target.value })}
              >
                {paymentTypes.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth type="date" label="Payment Date"
                InputLabelProps={{ shrink: true }}
                value={payForm.paymentDate}
                onChange={(e) => setPayForm({ ...payForm, paymentDate: e.target.value })}
              />
            </Grid>
            {/* Asked for ONLY when the invoice cannot answer it.
                Rent is billed by period and the payment has to say which months it settles,
                but this dialog is already pointed at one invoice, and that invoice records
                its own period — so it is read off there and shown above rather than typed
                again. Re-typing it is purely a chance to disagree with the invoice being
                paid.
                Reachable in two narrow cases: a rent invoice predating the rule that every
                rent invoice must carry a period, and switching the type to Rent on an
                invoice that never had one. Offering the fields beats a dead end where the
                server asks for a period the dialog gives no way to supply. */}
            {needsManualPeriod && (
              <>
                <Grid item xs={12}>
                  <Typography variant="caption" color="warning.main">
                    This invoice does not record a rental period, so please give the months
                    this payment covers.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth type="date" label="Period From" required
                    InputLabelProps={{ shrink: true }}
                    value={payForm.periodStart}
                    onChange={(e) => setPayForm({ ...payForm, periodStart: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth type="date" label="Period To" required
                    InputLabelProps={{ shrink: true }}
                    value={payForm.periodEnd}
                    onChange={(e) => setPayForm({ ...payForm, periodEnd: e.target.value })}
                  />
                </Grid>
              </>
            )}

            {/* The mode records HOW the money arrived; the account records WHERE it landed.
                The account is what the journal entry debits, which is why it is stored on
                the payment rather than looked up through the mode later — re-pointing a
                mode must never rewrite where past money appears to have gone. */}
            <Grid item xs={12}>
              <TextField
                fullWidth select required label="Mode of Payment"
                value={payForm.paymentModeId}
                onChange={(e) => {
                  // Changing the mode changes which accounts are on offer, so any account
                  // already chosen is cleared rather than left pointing at the wrong kind.
                  const mode = paymentModes.find((m) => m._id === e.target.value);
                  const matching = assetAccounts.filter((a) => a.accountCategory === mode?.accountCategory);
                  setPayForm({
                    ...payForm,
                    paymentModeId: e.target.value,
                    // Pre-selected when there is only one candidate — the common case for
                    // an agency with a single bank account.
                    receivedIntoAccountId: matching.length === 1 ? (matching[0]._id || matching[0].id) : '',
                  });
                }}
                helperText={
                  paymentModes.length === 0
                    ? 'No modes set up — an admin can add them under Payment Modes'
                    : 'How the money reached you'
                }
              >
                {paymentModes.map((mode) => (
                  <MenuItem key={mode._id} value={mode._id}>{mode.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth select required
                label={selectedModeCategoryLabel ? `${selectedModeCategoryLabel} Account` : 'Received Into'}
                value={payForm.receivedIntoAccountId}
                onChange={(e) => setPayForm({ ...payForm, receivedIntoAccountId: e.target.value })}
                disabled={!payForm.paymentModeId}
                helperText={
                  !payForm.paymentModeId
                    ? 'Choose a mode of payment first'
                    : accountsForSelectedMode.length === 0
                      ? `No ${selectedModeCategoryLabel} accounts set up — an admin can add one in the Chart of Accounts`
                      : 'Which account the money actually landed in'
                }
              >
                {accountsForSelectedMode.map((account) => (
                  <MenuItem key={account._id || account.id} value={account._id || account.id}>
                    {account.code} — {account.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <LatePenaltyPanel
                tenantId={payInvoice?.tenant?._id || payInvoice?.tenant}
                value={payPenalty}
                onChange={setPayPenalty}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Staff Comments (optional)" multiline rows={2}
                value={payForm.staffComments}
                onChange={(e) => setPayForm({ ...payForm, staffComments: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button onClick={handlePay} variant="contained" color="success">Record Payment</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default InvoiceManagement;


